import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Texel density, derived rather than eyeballed.
 *
 * Each forge sampler lays a fixed number of features across one texture tile, so the
 * only honest way to size a surface is to divide a real-world dimension by that
 * count. Eyeballing is what produces a map where a brick wall and the corrugated
 * sheet next to it disagree about how big a metre is, and the whole building then
 * reads as a model rather than a building.
 *
 *   corrugated  6 ribs/tile      × 85 mm pitch    -> 0.51 m
 *   brick       8 stretchers/tile × 225 mm        -> 1.80 m across
 *               16 courses/tile   × 75 mm         -> 1.20 m up   (needs tileV)
 *   wood        5 planks/tile     × 140 mm        -> 0.70 m across the grain
 *   ceramic     4 tiles/tile      × 200 mm        -> 0.80 m
 *   fabric      26 threads/tile   × 7 mm jute     -> 0.18 m
 *   concrete    78 aggregate/tile × 26 mm stone   -> 2.00 m
 *   asphalt     78 chippings/tile × 15 mm         -> 1.20 m
 *   dirt        40 pebbles/tile   × 35 mm         -> 1.40 m
 */
export const TEXEL = {
  corrugated: 0.51,
  brickU: 1.80,
  brickV: 1.20,
  woodU: 1.40,   // grain runs long, so the across-grain repeat is the one that matters
  woodV: 0.70,
  ceramic: 0.80,
  fabric: 0.18,
  canvas: 0.25,  // awnings and banners are a coarser weave than sacking
  concrete: 2.00,
  asphalt: 1.20,
  dirt: 1.40,
  metalPanel: 1.20,
  metalProp: 0.60,

  /**
   * A container side is *not* cladding. Wall sheet corrugates at about 76 mm, which
   * is what `corrugated` above is sized for; an ISO container corrugates at about
   * 275 mm, more than three times coarser. Drawing one at the other's density is why
   * a 6 m box read as picket fencing.
   *
   *   corrugated  6 ribs/tile × 275 mm -> 1.65 m
   *
   * It lives here, derived, rather than as a literal at the call site — a second
   * honest density is fine, a hand-tuned one at every call site is how the whole
   * problem started.
   */
  containerRib: 1.65,
};

/**
 * Mesh-building substrate for the level.
 *
 * Two ideas carry the whole level's performance budget:
 *
 * 1. **UVs are baked in world scale.** Instead of cloning a material per surface
 *    per tiling factor (which is what forces most hobby levels into thousands of
 *    draw calls), every primitive is generated with its UVs pre-multiplied by
 *    `worldSize / tileSize`. One material per surface then serves the entire map at
 *    a consistent texel density, and everything using it can be merged.
 *
 * 2. **Merge by surface, collide by box.** The visual side is a handful of merged
 *    `BufferGeometry` blobs; the collision side is the list of axis-aligned boxes we
 *    recorded on the way in. The two never have to agree in topology, which is what
 *    lets us build rotated awnings and cylindrical drums on top of an AABB physics
 *    world without lying to the player about where the walls are.
 *
 * Per-piece variation survives merging via a vertex-colour attribute, so a hundred
 * oil drums are one draw call and still no two are the same shade.
 */

export const TUNING = {
  /* Texel density. Walls and structure get one texture tile per 2 m; large ground
     planes are halved again so the tiling pattern doesn't strobe at distance;
     hand-props are denser because they're viewed from arm's length. */
  wallTile: 2.0,
  groundTile: 3.6,
  propTile: 1.0,
  fineTile: 0.6,

  /* Collision padding applied to cylinder/sphere AABBs so props feel solid but
     don't snag the player on empty air. */
  roundShrink: 0.86,
};

/** Forge surface -> PhysicsWorld material id (drives decals, ricochet and footsteps). */
export const PHYS_MAT = {
  concrete: 'concrete',
  brick: 'concrete',
  asphalt: 'concrete',
  tile: 'concrete',
  corrugated: 'metal',
  metalPainted: 'metal',
  gunmetal: 'metal',
  wood: 'wood',
  dirt: 'dirt',
  glass: 'glass',
  fabric: 'sand',
  polymer: 'wood',
};

/** Default [u, v] tile size per surface, derived from TEXEL above. */
const SURFACE_TEXEL = {
  corrugated: [TEXEL.corrugated, 0],
  brick: [TEXEL.brickU, TEXEL.brickV],
  wood: [TEXEL.woodU, TEXEL.woodV],
  tile: [TEXEL.ceramic, 0],
  fabric: [TEXEL.fabric, 0],
  concrete: [TEXEL.concrete, 0],
  asphalt: [TEXEL.asphalt, 0],
  dirt: [TEXEL.dirt, 0],
  metalPainted: [TEXEL.metalPanel, 0],
  polymer: [0.35, 0],
  glass: [0.7, 0],
};

/** [tile, tileV] for a surface, so callers never have to guess a repeat. */
export function surfaceTexel(surface) {
  return SURFACE_TEXEL[surface] ?? [TUNING.wallTile, 0];
}

const KEEP_ATTR = ['position', 'normal', 'uv', 'color'];
const WHITE = new THREE.Color(1, 1, 1);

/**
 * Shadow policy: **cast is per mesh, receive is per material.**
 *
 * The `'both' | 'cast' | 'receive' | 'none'` vocabulary at the call sites is a
 * statement about *casting only*, and this table is what reads it. The receive half
 * of the word is deliberately ignored, and that is not laziness — it is the fix for
 * two defects that were being reported as one.
 *
 * `castShadow` is genuinely per object: it only decides whether the depth pass walks
 * that mesh, so two batches drawn with the same material may disagree about it for
 * free. That is where the real decision lives — and at a 13-degree sun, where it has
 * to be made carefully, because every caster's height is multiplied by 1/tan(13) =
 * 4.33 on its way to the floor.
 *
 * `receiveShadow` is a different kind of flag, and treating it as the mirror of `cast`
 * is what produced the ground defect. Two things are true about it here:
 *
 * 1. It is not free to vary. Three.js compiles one program per *material* and uploads
 *    `receiveShadow` to it as a uniform, refreshing that uniform only when it differs
 *    from the value cached against the material (`WebGLRenderer`:
 *    `materialProperties.receiveShadow !== object.receiveShadow`). Every surface here
 *    merges into a handful of meshes that **share one forge material**, so batches
 *    that disagree make the renderer flip that uniform back and forth mid-frame for a
 *    material it believes it has already set up. Nine of this level's materials were
 *    in that state.
 *
 * 2. It was never a decision anybody made. `receive: false` arrived as the shadow of
 *    `cast: false` — and that put it on precisely the surfaces that needed it most.
 *    The 320 dirt patches, the road patching, the potholes and the tyre tracks are
 *    thousands of flat 1.5 cm quads lying on ground that *does* receive, and they were
 *    built `shadow: 'none'`. Inside a building's shadow they stayed at full noon
 *    brightness: a field of bright dashes scattered through the shade, which is the
 *    "regular dashed horizontal bands" in the bug report. Not aliasing, not bias, and
 *    not the awnings. Making receive universal took the measured dash-edge count in
 *    the market street from 45,722 to 1,340 — a 34x drop — and no single caster batch
 *    on the map moves that number at all.
 *
 * So: everything receives. There is no surface on this map that a shadow cannot
 * legitimately fall on. (The *shimmer* the same report describes is a second defect
 * with the same cause upstream — those overlays were also coplanar with the road they
 * lie on. See `TUNING.overlayStep` in Level.js.)
 */
const CASTS = { both: true, cast: true, receive: false, none: false };

/* -------------------------------------------------------------------------- */
/*                               primitives                                   */
/* -------------------------------------------------------------------------- */

/**
 * Box with world-scaled UVs. Each of the six faces is mapped by its own real-world
 * span so a 12 m wall and a 0.4 m kerb built from the same material show the same
 * grain size.
 */
export function boxGeo(w, h, d, tile = TUNING.wallTile, uOff = 0, vOff = 0, tileV = 0) {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes.uv;
  // `tileV` decouples the vertical repeat. Brick needs it: the sampler lays 8
  // stretchers across a tile but 16 courses up it, so a single square tile size
  // cannot give both a 215 mm brick and a 75 mm course.
  const tv = tileV || tile;
  // BoxGeometry face order: +X, -X, +Y, -Y, +Z, -Z — four verts each.
  const spans = [d, h, d, h, w, d, w, d, w, h, w, h];
  for (let f = 0; f < 6; f++) {
    const su = spans[f * 2] / tile;
    // The top and bottom faces used to take `tile` on both axes, which is a third
    // density nobody derived: a wood box came out with 280 mm planks on its lid and
    // 140 mm ones down its side, and that disagreement is exactly what makes a pallet
    // read as two different timbers. `tv` is the across-feature repeat whichever face
    // it lands on, so every face of a box now agrees about how big a plank is.
    const sv = spans[f * 2 + 1] / tv;
    for (let i = 0; i < 4; i++) {
      const k = f * 4 + i;
      uv.setXY(k, uv.getX(k) * su + uOff, uv.getY(k) * sv + vOff);
    }
  }
  uv.needsUpdate = true;
  return g;
}

/**
 * A timber *member* — a joist, batten, post or snapped beam — with its length along
 * local X.
 *
 * A board and a beam are not the same mapping problem. `boxGeo` treats a box as a
 * lump and gives every face the same two repeats, which is right for a panel and
 * wrong for a stick: on a 1.5 m beam it lays a plank boundary across the length every
 * 140 mm, so the beam reads as a stack of ten short blocks — a banded orange stripe
 * pattern rather than a piece of wood.
 *
 * Timber has a direction. The grain runs along the member, so the *long* axis takes
 * the along-grain repeat and both short axes take the across-grain one:
 *
 *   along grain   TEXEL.woodU = 1.40 m   (grain wander, no boundaries)
 *   across grain  TEXEL.woodV = 0.70 m   (5 planks/tile x 140 mm)
 *
 * which is the same pair of derived numbers `wood` uses everywhere else, just applied
 * per axis instead of per face.
 */
export function timberGeo(len, h, d) {
  const g = new THREE.BoxGeometry(len, h, d);
  const uv = g.attributes.uv;
  const A = TEXEL.woodU;
  const X = TEXEL.woodV;
  // Face order +X, -X, +Y, -Y, +Z, -Z, as [uSpan/uTile, vSpan/vTile] per face.
  const map = [
    [d / X, h / X], [d / X, h / X],   // end grain: across both ways
    [d / X, len / A], [d / X, len / A], // top/bottom: across the width, along the length
    [len / A, h / X], [len / A, h / X], // sides: along the length, across the depth
  ];
  for (let f = 0; f < 6; f++) {
    for (let i = 0; i < 4; i++) {
      const k = f * 4 + i;
      uv.setXY(k, uv.getX(k) * map[f][0], uv.getY(k) * map[f][1]);
    }
  }
  uv.needsUpdate = true;
  return g;
}

/**
 * A hanging cloth sheet: an (nu x nv) grid over a `w` x `d` rectangle in the local XZ
 * plane, with the height of every vertex given by `fn(u, v)` where u and v run -0.5..0.5
 * and 0..1 respectively.
 *
 * Awnings, tarpaulins and banners are the one family of props that cannot be built
 * from boxes. A rectangle has a single normal, so a flat awning renders one constant
 * value across its whole span whatever the light does — which is precisely the defect
 * this exists to fix. A sheet that sags has a different normal at every point, so the
 * sun rakes across it, the folds separate, and the eye reads *fabric under tension*
 * instead of a coloured card.
 *
 * `shade(u, v, y)` returns a per-vertex multiplier baked into the colour attribute, so
 * the fold valleys can be darkened and the crests lifted without a second draw call.
 */
export function sheetGeo(w, d, nu, nv, fn, o = {}) {
  const tile = o.tile ?? TEXEL.canvas;
  const shadeFn = o.shade;
  const base = o.color ?? WHITE;
  const nx = nu + 1;
  const nz = nv + 1;
  const pos = new Float32Array(nx * nz * 3);
  const uvs = new Float32Array(nx * nz * 2);
  const col = new Float32Array(nx * nz * 3);
  for (let j = 0; j < nz; j++) {
    const v = j / nv;
    for (let i = 0; i < nx; i++) {
      const u = i / nu - 0.5;
      const k = j * nx + i;
      const y = fn(u, v);
      pos[k * 3] = u * w;
      pos[k * 3 + 1] = y;
      pos[k * 3 + 2] = v * d;
      uvs[k * 2] = (u * w) / tile;
      uvs[k * 2 + 1] = (v * d) / tile;
      const s = shadeFn ? shadeFn(u, v, y) : 1;
      col[k * 3] = base.r * s;
      col[k * 3 + 1] = base.g * s;
      col[k * 3 + 2] = base.b * s;
    }
  }
  const idx = new Uint16Array(nu * nv * 6);
  let p = 0;
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const a = j * nx + i;
      idx[p++] = a; idx[p++] = a + nx; idx[p++] = a + 1;
      idx[p++] = a + 1; idx[p++] = a + nx; idx[p++] = a + nx + 1;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeVertexNormals();
  return g;
}

/** Cylinder with the side wrapped at world scale and the caps mapped from the top. */
export function cylGeo(rTop, rBot, h, seg = 12, tile = TUNING.propTile, openEnded = false, tileV = 0) {
  const g = new THREE.CylinderGeometry(rTop, rBot, h, seg, 1, openEnded);
  const uv = g.attributes.uv;
  const sideCount = (seg + 1) * 2;
  const circ = Math.PI * (rTop + rBot);
  // `tileV` decouples the lengthwise repeat: a timber pole wants its grain running
  // along the shaft, not banded around it like a barber pole.
  const tv = tileV || tile;
  for (let i = 0; i < uv.count; i++) {
    if (i < sideCount) {
      uv.setXY(i, uv.getX(i) * (circ / tile), uv.getY(i) * (h / tv));
    } else {
      const r = Math.max(rTop, rBot);
      uv.setXY(i, (uv.getX(i) - 0.5) * (2 * r / tile) + 0.5, (uv.getY(i) - 0.5) * (2 * r / tile) + 0.5);
    }
  }
  uv.needsUpdate = true;
  return g;
}

/** Sphere/ellipsoid with roughly world-scaled UVs (used for sandbags and rubble). */
export function sphereGeo(r, wSeg = 10, hSeg = 6, tile = TUNING.fineTile) {
  const g = new THREE.SphereGeometry(r, wSeg, hSeg);
  const uv = g.attributes.uv;
  const su = (2 * Math.PI * r) / tile;
  const sv = (Math.PI * r) / tile;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * su, uv.getY(i) * sv);
  uv.needsUpdate = true;
  return g;
}

/** Tube along a sagging catenary — cables, hoses, laundry lines. */
export function sagGeo(a, b, sag, radius = 0.03, seg = 10, tile = TUNING.fineTile) {
  const pts = [];
  const n = 6;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const p = new THREE.Vector3().lerpVectors(a, b, t);
    p.y -= Math.sin(t * Math.PI) * sag;
    pts.push(p);
  }
  const curve = new THREE.CatmullRomCurve3(pts);
  const g = new THREE.TubeGeometry(curve, seg, radius, 4, false);
  const uv = g.attributes.uv;
  const len = a.distanceTo(b);
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (len / tile), uv.getY(i));
  uv.needsUpdate = true;
  return g;
}

/** Trapezoid prism used for jersey barriers and blast-wall bases. */
export function taperGeo(wBottom, wTop, h, d, tile = TUNING.wallTile) {
  const shape = new THREE.Shape();
  shape.moveTo(-wBottom / 2, 0);
  shape.lineTo(wBottom / 2, 0);
  shape.lineTo(wTop / 2, h);
  shape.lineTo(-wTop / 2, h);
  shape.closePath();
  const g = new THREE.ExtrudeGeometry(shape, { depth: d, bevelEnabled: false, curveSegments: 1 });
  g.translate(0, 0, -d / 2);
  const uv = g.attributes.uv;
  // ExtrudeGeometry UVs are already in shape units (metres), so a plain divide works.
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) / tile, uv.getY(i) / tile);
  uv.needsUpdate = true;
  return g;
}

/* -------------------------------------------------------------------------- */
/*                                 batcher                                    */
/* -------------------------------------------------------------------------- */

function normalise(g, color) {
  g.clearGroups();
  g.morphAttributes = {};
  for (const name of Object.keys(g.attributes)) {
    if (!KEEP_ATTR.includes(name)) g.deleteAttribute(name);
  }
  if (!g.attributes.normal) g.computeVertexNormals();
  const n = g.attributes.position.count;
  if (!g.attributes.uv) {
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
  }
  // A geometry that arrived carrying its own per-vertex colour keeps it, provided the
  // caller did not ask for a flat tint. That is what lets a sagging sheet bake its own
  // fold shading in and still merge into the same batch as everything else — without
  // it, `normalise` would flatten every cloth back to one constant value, which is the
  // exact defect the sheet exists to cure.
  if (!(color === null && g.attributes.color && g.attributes.color.count === n)) {
    const c = new Float32Array(n * 3);
    const col = color || WHITE;
    for (let i = 0; i < n; i++) {
      c[i * 3] = col.r;
      c[i * 3 + 1] = col.g;
      c[i * 3 + 2] = col.b;
    }
    g.setAttribute('color', new THREE.BufferAttribute(c, 3));
  }
  if (!g.index) {
    const idx = new (n > 65535 ? Uint32Array : Uint16Array)(n);
    for (let i = 0; i < n; i++) idx[i] = i;
    g.setIndex(new THREE.BufferAttribute(idx, 1));
  }
  return g;
}

const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _v = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);

/** Compose a TRS matrix without allocating. */
export function trs(x, y, z, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) {
  _e.set(rx, ry, rz, 'YXZ');
  _q.setFromEuler(_e);
  return new THREE.Matrix4().compose(_v.set(x, y, z), _q, new THREE.Vector3(sx, sy, sz));
}

export class Batcher {
  constructor(ctx) {
    this.ctx = ctx;
    this.groups = new Map();
    this.scatters = [];
    /** Every solid AABB we registered — used for cover validation and debug. */
    this.solids = [];
    /** Boxes tagged as usable cover, consumed by the level to emit cover points. */
    this.coverBoxes = [];
    this.stats = { pieces: 0, colliders: 0, tris: 0 };
  }

  /* ------------------------------ raw geometry ----------------------------- */

  /**
   * Queue a geometry into the batch for `surface`.
   * `own` transfers ownership (no clone) — always pass true for freshly built geo.
   */
  add(surface, geo, matrix = null, color = null, shadow = 'both', own = true, matOverrides = null) {
    const g = own ? geo : geo.clone();
    if (matrix) g.applyMatrix4(matrix);
    normalise(g, color);
    // Keyed on the *cast* decision, not on the four-word vocabulary: since receive is
    // universal (see CASTS), 'receive' and 'none' describe the same mesh, and keying
    // them apart would only split one draw call into two identical ones.
    const cast = CASTS[shadow] ?? true;
    const key = `${surface}|${cast ? 'cast' : 'flat'}|${matOverrides ? JSON.stringify(matOverrides) : ''}`;
    let grp = this.groups.get(key);
    if (!grp) this.groups.set(key, (grp = { surface, cast, matOverrides, geos: [] }));
    grp.geos.push(g);
    this.stats.pieces++;
    return g;
  }

  /* -------------------------------- solids -------------------------------- */

  /**
   * An axis-aligned (or Y-rotated) box: visual geometry plus its collider.
   *
   * opts: { tile, color, rotY, collide, matId, shadow, cover, uOff, vOff, sx/sy/sz }
   * `cover: 'low'|'high'` marks the box as tactical cover for AI point generation.
   */
  solid(surface, cx, cy, cz, w, h, d, opts = {}) {
    const tx = surfaceTexel(surface);
    const tile = opts.tile ?? tx[0];
    const geo = boxGeo(w, h, d, tile, opts.uOff ?? 0, opts.vOff ?? 0, opts.tileV ?? (opts.tile ? 0 : tx[1]));
    const rotY = opts.rotY ?? 0;
    const m = _m4.makeRotationY(rotY).clone();
    m.setPosition(cx, cy, cz);
    this.add(surface, geo, m, opts.color, opts.shadow ?? 'both', true, opts.matOverrides);
    if (opts.collide !== false) {
      const c = Math.abs(Math.cos(rotY));
      const s = Math.abs(Math.sin(rotY));
      const hw = (w * c + d * s) / 2;
      const hd = (w * s + d * c) / 2;
      this.collider(cx - hw, cy - h / 2, cz - hd, cx + hw, cy + h / 2, cz + hd, opts.matId ?? PHYS_MAT[surface]);
      if (opts.cover) {
        this.coverBoxes.push({ x: cx, z: cz, hw, hd, top: cy + h / 2, y: cy - h / 2, height: opts.cover });
      }
    }
    return this;
  }

  /** Vertical cylinder (drums, pillars, pipes). Collider is a shrunken AABB. */
  post(surface, cx, cy, cz, r, h, opts = {}) {
    const seg = opts.seg ?? 12;
    const geo = cylGeo(opts.rTop ?? r, opts.rBot ?? r, h, seg, opts.tile ?? surfaceTexel(surface)[0], opts.open ?? false, opts.tileV ?? 0);
    const m = trs(cx, cy, cz, opts.rotX ?? 0, opts.rotY ?? 0, opts.rotZ ?? 0);
    this.add(surface, geo, m, opts.color, opts.shadow ?? 'both', true, opts.matOverrides);
    if (opts.collide !== false) {
      const rr = r * TUNING.roundShrink;
      this.collider(cx - rr, cy - h / 2, cz - rr, cx + rr, cy + h / 2, cz + rr, opts.matId ?? PHYS_MAT[surface]);
      if (opts.cover) {
        this.coverBoxes.push({ x: cx, z: cz, hw: rr, hd: rr, top: cy + h / 2, y: cy - h / 2, height: opts.cover });
      }
    }
    return this;
  }

  /** Explicit collider with no visual — ramps, awning approximations, invisible caps. */
  collider(minX, minY, minZ, maxX, maxY, maxZ, matId = 'concrete') {
    const min = new THREE.Vector3(minX, minY, minZ);
    const max = new THREE.Vector3(maxX, maxY, maxZ);
    this.ctx.physics.addBox(min, max, matId);
    this.solids.push({ min, max });
    this.stats.colliders++;
  }

  /** Register a cover face set for a hand-placed piece of geometry. */
  markCover(x, z, hw, hd, top, height, y = 0) {
    this.coverBoxes.push({ x, z, hw, hd, top, y, height });
  }

  /* Is a standing capsule at (x,z) intersecting anything solid? */
  blocked(x, z, y = 0.9, r = 0.42) {
    for (let i = 0; i < this.solids.length; i++) {
      const s = this.solids[i];
      if (y + 0.7 < s.min.y || y - 0.7 > s.max.y) continue;
      if (x + r > s.min.x && x - r < s.max.x && z + r > s.min.z && z - r < s.max.z) return true;
    }
    return false;
  }

  /* ------------------------------- instancing ------------------------------ */

  scatter(surface, geo, shadow = 'receive', matOverrides = null) {
    const s = new ScatterSet(surface, normalise(geo, WHITE), CASTS[shadow] ?? true, matOverrides);
    this.scatters.push(s);
    return s;
  }

  /* --------------------------------- build --------------------------------- */

  build(root) {
    const { forge } = this.ctx;
    const meshes = [];
    for (const grp of this.groups.values()) {
      if (!grp.geos.length) continue;
      const merged = mergeGeometries(grp.geos, false);
      if (!merged) {
        console.warn('[level] merge failed for', grp.surface);
        continue;
      }
      merged.computeBoundingBox();
      merged.computeBoundingSphere();
      this.stats.tris += (merged.index ? merged.index.count : merged.attributes.position.count) / 3;
      const mat = forge.material(grp.surface, { vertexColors: true, ...(grp.matOverrides || {}) });
      const mesh = new THREE.Mesh(merged, mat);
      mesh.castShadow = grp.cast;
      mesh.receiveShadow = true;
      mesh.userData.noCollide = true;
      mesh.name = `batch:${grp.surface}:${grp.cast ? 'cast' : 'flat'}`;
      root.add(mesh);
      meshes.push(mesh);
      for (const g of grp.geos) g.dispose();
      grp.geos.length = 0;
    }
    for (const s of this.scatters) {
      const mesh = s.build(forge);
      if (mesh) {
        root.add(mesh);
        meshes.push(mesh);
        this.stats.tris += (s.geo.index.count / 3) * s.items.length;
      }
    }
    this.meshes = meshes;
    return meshes;
  }
}

/** InstancedMesh accumulator for high-count, low-importance props. */
class ScatterSet {
  constructor(surface, geo, cast, matOverrides) {
    this.surface = surface;
    this.geo = geo;
    this.cast = cast;
    this.matOverrides = matOverrides;
    this.items = [];
  }
  place(matrix, color = WHITE) {
    this.items.push({ m: matrix, c: color });
    return this;
  }
  build(forge) {
    if (!this.items.length) return null;
    const mat = forge.material(this.surface, { vertexColors: true, ...(this.matOverrides || {}) });
    const mesh = new THREE.InstancedMesh(this.geo, mat, this.items.length);
    for (let i = 0; i < this.items.length; i++) {
      mesh.setMatrixAt(i, this.items[i].m);
      mesh.setColorAt(i, this.items[i].c);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.castShadow = this.cast;
    mesh.receiveShadow = true;
    mesh.userData.noCollide = true;
    mesh.name = `scatter:${this.surface}`;
    mesh.computeBoundingSphere();
    return mesh;
  }
}

/* -------------------------------------------------------------------------- */
/*                              colour helpers                                */
/* -------------------------------------------------------------------------- */

/**
 * Vertex colour multiplies the forge albedo, so a tint has to be centred on 1.0.
 * Feeding a "looks about right" hex straight in is the classic trap: 0x9c9382 is a
 * plausible concrete colour but as a multiplier it is 0.32, so the wall renders
 * three times too dark and the whole map turns to mud.
 *
 * The obvious correction — normalise every hex to unit luminance — trades that bug
 * for a worse one. Unit luminance is a terrible basis for a *saturated* colour: a
 * mid blue divided by its own luminance puts 14.0 in the blue channel, so the only
 * way to stop it blowing out is to desaturate everything by half. Do that and the
 * whole palette collapses into one band of tan: tyres come out mid-brown, three
 * container colours become one, and a green shrub renders grey.
 *
 * So value and chroma are carried separately here.
 *
 *   chroma  the hex normalised on its **maximum channel** — which is precisely the
 *           multiplier that reproduces the authored colour on a neutral albedo, and
 *           never exceeds 1.0 in any channel. `sat: 1` therefore means "as authored".
 *   value   a curve on the hex's own sRGB level, with a gamma above 1 so the
 *           authored light-to-dark spread survives rather than compressing. Soot
 *           lands near 0.09 and reads black; a paint stencil lands above 1.0 and
 *           reads like paint.
 */
const _WHITE = new THREE.Color(1, 1, 1);

/* The sRGB level that maps to a 1.0 multiplier: a hex at this brightness leaves the
   forge albedo alone, and everything else moves relative to it. */
const REF = 0.60;
/* Above 1.0, so the distance between a dark hex and a light one is preserved rather
   than crushed. This single number is the difference between a palette and a mood. */
const GAMMA = 1.4;

function modulator(hex, sat, lum) {
  const c = new THREE.Color(hex);
  const mx = Math.max(c.r, c.g, c.b, 1e-5);
  c.multiplyScalar(1 / mx);
  if (sat < 1) c.lerp(_WHITE, 1 - sat);
  const level = Math.max((hex >> 16) & 255, (hex >> 8) & 255, hex & 255) / 255;
  const v = lum ?? Math.min(1.7, Math.max(0.03, Math.pow(level / REF, GAMMA)));
  return c.multiplyScalar(v);
}

/** Neutral-hue tint whose level is taken from the hex unless `lum` overrides it. */
export function tint(hex, o = {}) {
  return modulator(hex, o.sat ?? 0.72, o.lum);
}

/** Explicitly darker than the base surface — soot, rubber, shadowed glass. */
export function shade(hex, lum, sat = 0.72) {
  return modulator(hex, sat, lum);
}

/**
 * Full authored chroma, for the handful of things that are genuinely *painted*:
 * a bus livery band, a market banner, a hazard stripe, a fire point. A back-lot is
 * a desaturated place, which is exactly why the two or three saturated objects in
 * frame have to be allowed to be saturated — otherwise there is no dust to read.
 */
export function accent(hex, o = {}) {
  return modulator(hex, o.sat ?? 1.0, o.lum);
}

/** Per-piece jitter around a tint — kills the clone-army look after merging. */
export function vary(rng, hex, amount = 0.09, hueAmount = 0.02, o = {}) {
  const c = modulator(hex, o.sat ?? 0.82, o.lum);
  c.multiplyScalar(Math.max(0.35, 1 + (rng() - 0.5) * 2 * amount));
  if (hueAmount) {
    const d = (rng() - 0.5) * hueAmount * 6;
    c.r = Math.max(0, c.r * (1 + d));
    c.b = Math.max(0, c.b * (1 - d));
  }
  return c;
}
